import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';

/**
 * Redeploy version-bump chooser.
 *
 * Shown before an in-place redeploy so the manager picks which semver segment
 * to increment. Mirrors the server's bumpVersion() rules so the previewed
 * "next" version matches exactly what the deploy will stamp:
 *   • major → increment MAJOR, reset MINOR + PATCH (1.4.2 → 2.0.0)
 *   • minor → increment MINOR, reset PATCH          (1.4.2 → 1.5.0)
 *   • patch → increment PATCH                        (1.4.2 → 1.4.3)
 *
 * On open it also calls GET /api/deploy/:appName/redeploy-check to see whether
 * the branch's current commit differs from what was last deployed. When the
 * code is unchanged, redeploying would be a no-op, so we warn and disable the
 * Redeploy button (the server enforces the same rule with a 409).
 *
 * Props:
 *   appName        registry key (for the change-check)
 *   displayName    friendly app name (header)
 *   currentVersion current version string (may be empty for a never-versioned app)
 *   apiUrl         API base URL
 *   authFetch      (url, opts) => fetch, injects the Okta bearer token
 *   onCancel       dismiss without redeploying
 *   onConfirm      (bumpType) => void — proceed with the chosen bump
 */
const OPTIONS = [
  { type: 'major', label: 'Major', hint: 'Breaking changes', color: '#ef9a9a' },
  { type: 'minor', label: 'Minor', hint: 'New features (backward-compatible)', color: '#81c784' },
  { type: 'patch', label: 'Patch', hint: 'Bug fixes / small tweaks', color: '#7cc4ff' },
];

// Normalize to MAJOR.MINOR.PATCH (mirrors server normalizeVersion; defaults to 1.0.0).
function normalize(v) {
  const raw = String(v || '').trim().replace(/^v/i, '');
  if (!/^\d{1,6}(\.\d{1,6}){0,2}$/.test(raw)) return '1.0.0';
  const [maj = '0', min = '0', pat = '0'] = raw.split('.');
  return `${parseInt(maj, 10)}.${parseInt(min, 10)}.${parseInt(pat, 10)}`;
}

function bump(v, type) {
  const [maj, min, pat] = normalize(v).split('.').map((n) => parseInt(n, 10));
  if (type === 'major') return `${maj + 1}.0.0`;
  if (type === 'patch') return `${maj}.${min}.${pat + 1}`;
  return `${maj}.${min + 1}.0`;
}

// True only when the app already has a real recorded version. `normalize('')`
// returns 1.0.0, so we must test the raw value separately: a never-versioned
// (legacy) app won't be bumped — the server initializes it at 1.0.0 (action
// 'initial') on its first versioned redeploy, regardless of the chosen type.
function isVersioned(v) {
  const raw = String(v || '').trim().replace(/^v/i, '');
  return /^\d{1,6}(\.\d{1,6}){0,2}$/.test(raw);
}
const INITIAL_VERSION = '1.0.0';

const RedeployVersionModal = ({ appName, displayName, currentVersion, apiUrl, authFetch, onCancel, onConfirm }) => {
  const [selected, setSelected] = useState('minor');
  // Change-check state: null while loading; then { changed, comparable, currentSha, lastSha }.
  const [check, setCheck] = useState(null);
  const [checkError, setCheckError] = useState(null);
  const base = normalize(currentVersion);
  const versioned = isVersioned(currentVersion);

  // Ask the server whether the branch has new commits since the last deploy.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await authFetch(`${apiUrl}/api/deploy/${encodeURIComponent(appName)}/redeploy-check`);
        const data = await res.json().catch(() => ({}));
        if (!alive) return;
        if (!res.ok) { setCheckError(data.error || `HTTP ${res.status}`); return; }
        setCheck(data);
      } catch (err) {
        if (alive) setCheckError(err.message);
      }
    })();
    return () => { alive = false; };
  }, [authFetch, apiUrl, appName]);

  // Close on Escape (matches the app-wide modal behavior).
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onCancel(); };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onCancel]);

  const checking = check === null && !checkError;
  const noChanges = !!check && check.changed === false;
  // Block until the check resolves so a fast click can't race past a no-op.
  const blocked = checking || noChanges;

  return ReactDOM.createPortal(
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 10000, display: 'flex',
        alignItems: 'center', justifyContent: 'center', padding: 16,
        backgroundColor: 'rgba(0,0,0,0.6)',
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Choose redeploy type"
        style={{
          width: '100%', maxWidth: 460,
          backgroundColor: '#14141f', border: '1px solid #333', borderRadius: 12,
          boxShadow: '0 12px 40px rgba(0,0,0,0.5)',
        }}
      >
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #2a2a3a' }}>
          <h3 style={{ margin: 0, fontSize: 16, color: '#fff' }}>What type of redeploy?</h3>
          <div style={{ fontSize: 12, color: '#8a97a8', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {versioned
              ? <>{displayName} · current <code style={{ color: '#80cbc4' }}>v{base}</code></>
              : <>{displayName} · not yet versioned · initializes at <code style={{ color: '#80cbc4' }}>v{INITIAL_VERSION}</code></>}
          </div>
        </div>

        {/* Change-check banner */}
        {checking && (
          <div style={{ padding: '10px 20px', fontSize: 12, color: '#8a97a8', borderBottom: '1px solid #2a2a3a' }}>
            ⏳ Checking for new commits since the last deploy…
          </div>
        )}
        {noChanges && (
          <div style={{ padding: '12px 20px', fontSize: 12.5, color: '#ffcc80', background: 'rgba(255,152,0,0.08)', borderBottom: '1px solid #3a2e1a' }}>
            ⚠️ <strong>No code changes</strong> since v{check.lastVersion || base}. The{' '}
            <code style={{ color: '#ffcc80' }}>{check.branch}</code> branch is still at commit{' '}
            <code style={{ color: '#ffcc80' }}>{(check.currentSha || '').slice(0, 7)}</code>. Push new
            commits before redeploying — Lyric won't mint a new version for identical code.
          </div>
        )}
        {checkError && (
          <div style={{ padding: '10px 20px', fontSize: 12, color: '#8a97a8', borderBottom: '1px solid #2a2a3a' }}>
            Couldn't verify changes ({checkError}). You can still redeploy.
          </div>
        )}
        {!versioned && !noChanges && (
          <div style={{ padding: '12px 20px', fontSize: 12.5, color: '#80cbc4', background: 'rgba(128,203,196,0.08)', borderBottom: '1px solid #1f3a37' }}>
            ℹ️ This app hasn't been versioned yet. This redeploy <strong>initializes</strong> it at{' '}
            <code style={{ color: '#80cbc4' }}>v{INITIAL_VERSION}</code> — the bump type below applies to <em>future</em> redeploys.
          </div>
        )}

        {/* Options */}
        <div style={{ padding: '14px 20px', display: 'flex', flexDirection: 'column', gap: 10, opacity: noChanges ? 0.5 : 1, pointerEvents: noChanges ? 'none' : 'auto' }}>
          {OPTIONS.map((opt) => {
            const isSel = selected === opt.type;
            const next = versioned ? bump(base, opt.type) : INITIAL_VERSION;
            return (
              <button
                key={opt.type}
                onClick={() => setSelected(opt.type)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left',
                  padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
                  background: isSel ? 'rgba(21, 34, 56, 0.9)' : 'rgba(255,255,255,0.02)',
                  border: `1px solid ${isSel ? opt.color : '#2a2a3a'}`,
                }}
              >
                <span aria-hidden="true" style={{
                  width: 16, height: 16, borderRadius: '50%', flexShrink: 0,
                  border: `2px solid ${isSel ? opt.color : '#555'}`,
                  background: isSel ? opt.color : 'transparent',
                }} />
                <span style={{ flex: 1 }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>{opt.label}</span>
                  <span style={{ display: 'block', fontSize: 11, color: '#8a97a8' }}>{opt.hint}</span>
                </span>
                <code style={{ fontSize: 13, fontWeight: 700, color: opt.color, whiteSpace: 'nowrap' }}>
                  {versioned ? <>v{base} → v{next}</> : <>→ v{INITIAL_VERSION}</>}
                </code>
              </button>
            );
          })}
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '12px 20px', borderTop: '1px solid #2a2a3a' }}>
          <button
            onClick={onCancel}
            style={{ padding: '8px 16px', fontSize: 13, fontWeight: 600, background: 'none', color: '#aaa', border: '1px solid #444', borderRadius: 6, cursor: 'pointer' }}
          >
            {noChanges ? 'Close' : 'Cancel'}
          </button>
          <button
            onClick={() => onConfirm(selected)}
            disabled={blocked}
            style={{
              padding: '8px 16px', fontSize: 13, fontWeight: 700, borderRadius: 6,
              background: blocked ? '#222' : '#1a2030',
              color: blocked ? '#666' : '#4fc3f7',
              border: `1px solid ${blocked ? '#333' : '#4fc3f7'}`,
              cursor: blocked ? 'not-allowed' : 'pointer',
            }}
          >
            {checking ? 'Checking…' : `🔄 Redeploy as v${versioned ? bump(base, selected) : INITIAL_VERSION}`}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default RedeployVersionModal;

